import { useState } from 'react';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Check } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { PageContainer } from "@/components/layout/PageContainer";
import { useAuth } from "@/contexts/AuthContext";
import { getIdToken } from "@/lib/firebase";

const plans = [
  {
    id: 'basic-monthly',
    name: '베이직',
    price: '$29',
    period: '/월',
    description: '한 지역에서 광고를 시작하는 소규모 업체용',
    features: [
      '관리자 소재·목적지 검수',
      '채팅 시작 지면',
      '1개 지역·태그 타기팅',
      '실제 노출·클릭 기본 보고',
      '이메일 운영 지원',
    ],
  },
  {
    id: 'standard-monthly',
    name: '스탠다드',
    price: '$59',
    period: '/월',
    description: '여러 생활정보 지면에 도달하려는 업체용',
    features: [
      '관리자 소재·목적지 검수',
      '협의된 최대 2개 지면',
      '지역·최대 2개 태그 타기팅',
      '실제 노출·클릭 주간 보고',
      '우선 운영 지원',
    ],
    popular: true,
  },
  {
    id: 'premium-monthly',
    name: '프리미엄',
    price: '$99',
    period: '/월',
    description: '지역·지면을 함께 최적화하는 집중 운영형',
    features: [
      '관리자 소재·목적지 검수',
      '협의된 복수 지면 운영',
      '지역·태그 맞춤 타기팅',
      '실제 노출·클릭 상세 보고',
      '우선순위 송출 설정',
      '전담 운영 지원',
    ],
  },
];

export default function AdvertisePage() {
  const { toast } = useToast();
  const { user, loading: authLoading } = useAuth();
  const [selectedPlan, setSelectedPlan] = useState(plans[1].id);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    business_name: '',
    contact_email: '',
    region: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.business_name) {
      toast({
        title: '업체 정보가 필요합니다',
        description: '업체명을 입력해 주세요.',
        variant: 'destructive',
      });
      return;
    }

    if (!user) {
      toast({
        title: '로그인이 필요합니다',
        description: '광고 구독과 업체 소유권을 안전하게 연결하려면 먼저 로그인해 주세요.',
        variant: 'destructive',
      });
      return;
    }

    setLoading(true);

    try {
      const token = await getIdToken(true);
      if (!token) throw new Error('로그인 토큰을 확인할 수 없습니다. 다시 로그인해 주세요.');
      const response = await fetch('/api/billing/create-checkout-session', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          business_name: form.business_name,
          region: form.region,
          plan_id: selectedPlan,
        }),
      });

      const data = await response.json() as { ok?: boolean; url?: string; error?: string };

      if (data.ok && data.url) {
        window.location.href = data.url;
      } else {
        toast({
          title: '결제 준비 오류',
          description: data.error || '결제 세션을 만들지 못했습니다.',
          variant: 'destructive',
        });
      }
    } catch (error) {
      toast({
        title: '결제 연결 오류',
        description: error instanceof Error ? error.message : '결제 서비스에 연결하지 못했습니다.',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <PageContainer size="default">
      <div className="text-center mb-12">
        <h1 className="text-4xl font-bold mb-4">OZK 동네업체 광고</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
          업체의 실제 영업 지역과 관련 생활정보 지면을 연결해 검수된 광고를 운영합니다.
        </p>
      </div>

      {/* Business Information Form */}
      <Card className="mb-8">
        <CardHeader>
          <CardTitle>업체 정보</CardTitle>
          <CardDescription>결제와 광고 운영에 연결할 실제 업체 정보를 입력해 주세요.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="business_name">업체명 *</Label>
              <Input
                id="business_name"
                data-testid="input-business-name"
                placeholder="업체명"
                value={form.business_name}
                onChange={(e) =>
                  setForm((f) => ({ ...f, business_name: e.target.value }))
                }
                required
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">연락 이메일 *</Label>
              <Input
                id="contact_email"
                data-testid="input-contact-email"
                type="email"
                placeholder="email@example.com"
                value={user?.email || form.contact_email}
                onChange={(e) => setForm((f) => ({ ...f, contact_email: e.target.value }))}
                disabled={Boolean(user?.email)}
                required
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="region">영업 지역 (선택)</Label>
            <Input
              id="region"
              data-testid="input-region"
              placeholder="예: NSW, VIC, QLD"
              value={form.region}
              onChange={(e) =>
                setForm((f) => ({ ...f, region: e.target.value.toUpperCase() }))
              }
            />
          </div>

          {!authLoading && !user && <p className="rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-900">결제를 진행하려면 먼저 로그인해야 합니다. 로그인 후 구독 정보가 업체 계정과 자동으로 연결됩니다.</p>}
        </CardContent>
      </Card>

      {/* Pricing Plans */}
      <h2 className="text-2xl font-bold text-center mb-6">광고 플랜 선택</h2>
      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {plans.map((plan) => (
          <Card
            key={plan.id}
            className={`relative cursor-pointer transition-all ${
              selectedPlan === plan.id
                ? 'border-primary shadow-lg scale-105'
                : 'hover-elevate'
            }`}
            onClick={() => setSelectedPlan(plan.id)}
            data-testid={`card-plan-${plan.id}`}
          >
            {plan.popular && (
              <Badge className="absolute -top-3 left-1/2 -translate-x-1/2">
                가장 많이 선택
              </Badge>
            )}
            <CardHeader>
              <CardTitle>{plan.name}</CardTitle>
              <CardDescription>{plan.description}</CardDescription>
              <div className="mt-4">
                <span className="text-4xl font-bold">{plan.price}</span>
                <span className="text-muted-foreground">{plan.period}</span>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {plan.features.map((feature, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <Check className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                    <span className="text-sm">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
            <CardFooter>
              <Button
                className="w-full"
                variant={selectedPlan === plan.id ? 'default' : 'outline'}
                data-testid={`button-select-${plan.id}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedPlan(plan.id);
                }}
              >
                {selectedPlan === plan.id ? '선택됨' : '선택'}
              </Button>
            </CardFooter>
          </Card>
        ))}
      </div>

      {/* Submit Button */}
      <div className="text-center">
        <Button
          size="lg"
          onClick={handleSubmit}
          disabled={loading || authLoading || !user}
          data-testid="button-proceed-checkout"
          className="px-12"
        >
          {loading ? '결제 준비 중...' : 'Stripe로 안전하게 결제하기'}
        </Button>
        <p className="text-sm text-muted-foreground mt-4">
          결제 후 OZK 운영팀이 업체·소재·목적지를 확인하고 승인된 기간부터 송출합니다. Stripe 구독은 언제든 취소할 수 있습니다.
        </p>
      </div>
    </PageContainer>
  );
}
